import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MapPin, ShieldCheck, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial } from "@/components/HubEditorial";
import { LocalLinks } from "@/components/LocalLinks";
import { bodenSections } from "@/data/lokala-djup";

const URL_SELF = "https://www.akhusbilfritid.se/husbil-boden";

const FAQ = [
  {
    q: "Finns det husbilshandlare i Boden?",
    a: "Utbudet i Boden är begränsat. Många Bodensare kör till oss i Kalix — ca 110 km bort — för att vi är officiell Rapido-återförsäljare och har både nya och kontrollerade begagnade husbilar i lager.",
  },
  {
    q: "Hur långt är det från Boden till er i Kalix?",
    a: "Ca 110 km, ungefär 1 timme 20 minuter. Ring innan du åker så står de bilar du är intresserad av framme.",
  },
  {
    q: "Kan ni leverera husbilen till Boden?",
    a: "Ja, transport ordnas mot en fast avgift. Vi räknar ihop total kostnad tillsammans med köpet.",
  },
  {
    q: "Servar och fukttestar ni husbilar från Boden?",
    a: "Ja. Vi tar in husbilar från hela Norrbotten för fukttest, vinterservice, gasol- och värmesystem, elarbeten och chassiservice. Boka i god tid inför säsongen.",
  },
  {
    q: "Har ni både nya och begagnade husbilar för Bodensare?",
    a: "Ja. Nya Rapido från fabrik och kontrollerade begagnade husbilar från flera märken — Rapido, Hymer, Kabe, Adria, Bürstner m.fl. — alla med fuktprotokoll.",
  },
];

export const Route = createFileRoute("/husbil-boden")({
  head: () => ({
    meta: [
      { title: "Husbil Boden – AK Husbil & Fritid i Kalix" },
      {
        name: "description",
        content:
          "Söker du husbil i Boden? AK Husbil & Fritid i Kalix — 110 km bort, officiell Rapido-återförsäljare, familjeägda sedan 2004. Ring 070-862 97 43.",
      },
      { name: "keywords", content: "husbil Boden, husbilar Boden, köpa husbil Boden, husvagn Boden, husbilshandlare Boden, begagnad husbil Boden" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Husbil Boden – AK Husbil & Fritid" },
      { property: "og:description", content: "Familjeägd husbilshandlare för Boden. Officiell Rapido-återförsäljare, kontrollerade fordon, transport ordnas." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: "AK Husbil & Fritid",
          url: URL_SELF,
          areaServed: ["Boden", "Luleå", "Älvsbyn", "Norrbotten"],
          address: {
            "@type": "PostalAddress",
            streetAddress: "Stabsvägen 5",
            addressLocality: "Kalix",
            postalCode: "952 51",
            addressCountry: "SE",
          },
          telephone: "+46708629743",
        }),
      },
    ],
  }),
  component: HusbilBodenPage,
});

function HusbilBodenPage() {
  return (
    <>
      <PageHeader
        label="Husbil · Boden · Norrbotten"
        title={<>Husbil för dig i <span className="font-italic-display text-accent">Boden</span>.</>}
        sub="AK Husbil & Fritid ligger i Kalix — 110 km från Boden. Officiell Rapido-återförsäljare, familjeägda sedan 2004. Vi vet vad som fungerar i norrländskt klimat."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/fordon" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Se husbilar i lager <ArrowRight size={14} />
          </Link>
          <Link to="/rapido" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Rapido-modeller
          </Link>
        </div>
      </section>

      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="eyebrow">Från Boden till oss</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">110 km. <span className="font-italic-display text-accent">Värt resan.</span></h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Vi ligger i Kalix, öster om Luleå. Bodensare väljer oss för att vi är officiell Rapido-återförsäljare, för att våra begagnade fordon alltid är fukttestade och för att vi svarar när telefonen ringer.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Officiell Rapido-återförsäljare för norra Sverige",
                "Kontrollerade begagnade husbilar & husvagnar",
                "Fukttest, gasol/värme, el och chassiservice",
                "Personlig service — samma människor varje gång",
                "Transport till Boden ordnas mot fast avgift",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-foreground/85">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <ShieldCheck className="text-accent" size={32} />
            <h3 className="mt-6 font-display text-3xl">Kör hit</h3>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Stabsvägen 5, 952 51 Kalix. Från Boden: kör mot Luleå och vidare på E4 mot Kalix, ca 110 km totalt. Ring först så står bilarna framme.
            </p>
            <dl className="mt-8 space-y-4 text-sm">
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground flex items-center gap-2"><MapPin size={14} /> Från Boden C</dt>
                <dd className="font-medium">110 km · ~1 h 20 min</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground flex items-center gap-2"><Clock size={14} /> Öppet</dt>
                <dd className="font-medium">Enligt överenskommelse</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Telefon</dt>
                <dd className="font-medium">070-862 97 43</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">E-post</dt>
                <dd className="font-medium">info@akhusbilfritid.se</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">Husbil <span className="font-italic-display text-accent">Boden</span> — vad du behöver veta.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQ.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="pt-16 md:pt-20">
        <HubEditorial eyebrow="Lokal fördjupning" sections={bodenSections} />
      </div>

      <LocalLinks
        keyId="husbil-boden"
        text="husbil boden norrbotten fukttest vinterförvaring besiktning ställplatser finansiering leasing"
        currentHref="/husbil-boden"
      />

      <section className="container-edge py-16 md:py-24">

        <div className="rounded-3xl border border-border bg-foreground p-8 md:p-14 text-background">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>Nästa steg</p>
          <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">Kör upp från Boden — eller ring först.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Vi bokar in dig, säkerställer att modellerna du är intresserad av står framme och lägger tiden på just din användning.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90">
              <Phone size={16} /> 070-862 97 43
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10">
              <MapPin size={16} /> Boka besök i Kalix
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
